import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DownloadSimple } from '@phosphor-icons/react';
import { API } from './adminConstants';
import { CsvExportButton } from './CsvHelpers';

export function ApplicationsView() {
  const { getAuthHeader } = useAuth();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = async () => { const r = await axios.get(`${API}/admin/applications`, { headers: getAuthHeader() }); setApps(r.data); };
  useEffect(() => { load(); }, []);

  const filtered = filter === 'all' ? apps : apps.filter(a => a.status === filter);

  const handleStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/applications/${id}/status?new_status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('Status updated');
      load();
    } catch { toast.error('Failed'); }
  };

  const downloadCv = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/applications/${id}/cv`, { headers: getAuthHeader() });
      const a = document.createElement('a');
      a.href = `data:application/octet-stream;base64,${res.data.file_base64}`;
      a.download = res.data.filename; a.click();
    } catch { toast.error('No CV attached or download failed'); }
  };

  return (
    <div data-testid="applications-view">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Applications</h1><p className="text-slate-500 text-sm">{apps.length} total applications</p></div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="shortlisted">Shortlisted</SelectItem>
              <SelectItem value="hired">Hired</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <CsvExportButton endpoint="/csv/export/applications" label="Export" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="divide-y divide-slate-100">
          {filtered.map(a => (
            <div key={a.id} className="p-5" data-testid={`application-${a.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-slate-900">{a.first_name} {a.last_name}</p>
                  <p className="text-sm text-slate-500">{a.email}{a.phone ? ` | ${a.phone}` : ''}</p>
                  <p className="text-xs text-lime-600 mt-0.5">Applied for: {a.job_title}</p>
                  <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <div className="flex items-center gap-2">
                  {a.cv_filename && <Button variant="outline" size="sm" onClick={() => downloadCv(a.id)} data-testid={`download-cv-${a.id}`}><DownloadSimple size={14} className="mr-1" />CV</Button>}
                  <Select value={a.status} onValueChange={v => handleStatus(a.id, v)}>
                    <SelectTrigger className={`h-8 text-xs w-28 ${a.status === 'hired' ? 'border-lime-300 text-lime-700' : a.status === 'rejected' ? 'border-red-300 text-red-700' : 'border-slate-200'}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewing">Reviewing</SelectItem>
                      <SelectItem value="shortlisted">Shortlisted</SelectItem>
                      <SelectItem value="hired">Hired</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {a.cover_letter && <p className="mt-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{a.cover_letter}</p>}
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-slate-500">No applications found</div>}
        </div>
      </div>
    </div>
  );
}
